import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ImageIcon, RefreshCw, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useIsVerified, useVerificationStatus } from '@/hooks/use-verification';
import { VerificationGateModal } from '@/components/verification/VerificationGateModal';
import { VerificationForm } from '@/components/verification/VerificationForm';
import { supabase } from '@/integrations/supabase/client';

interface ComposerProps {
  showComposer?: boolean;
  hidePostButtons?: boolean;
  mode?: 'all' | 'chrono';
  onModeChange?: (m: 'all' | 'chrono') => void;
  categories?: string[];
  category?: string;
  onCategoryChange?: (c: string) => void;
  onCreate?: (text: string, file?: File) => Promise<void>;
  showRefresh?: boolean;
  onRefresh?: () => void;
  onCompose?: () => void;
  viewModeButtons?: React.ReactNode;
}

const Composer: React.FC<ComposerProps> = ({
  showComposer = false,
  hidePostButtons = false,
  mode,
  onModeChange,
  categories,
  category,
  onCategoryChange,
  onCreate,
  showRefresh,
  onRefresh,
  onCompose,
  viewModeButtons,
}) => {
  const { profile, user } = useAuth();
  const { isVerified } = useIsVerified();
  const { hasPendingRequest, isRejected } = useVerificationStatus();
  
  // Verification modal states
  const [showVerificationGate, setShowVerificationGate] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showPostPrompt, setShowPostPrompt] = useState(false);
  const [combo, setCombo] = useState(0);
  const [comboAnimKey, setComboAnimKey] = useState(0);
  const comboTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textWidthRef = useRef<HTMLSpanElement | null>(null);
  const [textWidth, setTextWidth] = useState(0);
  const chipsRef = useRef<HTMLDivElement>(null);
  
  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);
  // More drawer state
  const [showMore, setShowMore] = useState(false);
  const [tagQuery, setTagQuery] = useState('');
  const [recentTags, setRecentTags] = useState<string[]>([]);

  // Fresh verification check (same as BulletinForm)
  const checkVerifiedFresh = async (): Promise<boolean> => {
    if (!user?.id) return !!isVerified;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_verified_human')
        .eq('id', user.id)
        .maybeSingle();
      if (error) {
        console.warn('[Composer] Fresh verify check error:', error);
        return !!isVerified;
      }
      const fresh = !!data?.is_verified_human;
      console.log('[Composer] Fresh is_verified_human:', fresh);
      return fresh;
    } catch (e) {
      console.warn('[Composer] Fresh verify check exception:', e);
      return !!isVerified;
    }
  };

  // Handle compose with verification check
  const handleComposeClick = async () => {
    console.log('[Composer] Compose clicked, checking verification');
    const freshVerified = await checkVerifiedFresh();
    
    if (!freshVerified) {
      console.log('[Composer] User not verified, showing gate modal');
      setShowVerificationGate(true);
      return;
    }
    
    console.log('[Composer] User verified, opening compose modal');
    if (onCompose) {
      onCompose();
    } else if (!showPostPrompt) {
      setShowPostPrompt(true);
    }
  };

  const handleGetVerified = () => {
    setShowVerificationGate(false);
    setShowVerificationForm(true);
  };

  const handleVerificationFormSuccess = () => {
    setShowVerificationForm(false);
    setShowVerificationGate(true);
  };

  // Load recent tags from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('fomo_recent_tags');
      if (raw) setRecentTags(JSON.parse(raw));
    } catch {}
  }, []);

  const pushRecentTag = (tag: string) => {
    try {
      const next = [tag, ...recentTags.filter(t => t !== tag)].slice(0, 12);
      setRecentTags(next);
      localStorage.setItem('fomo_recent_tags', JSON.stringify(next));
    } catch {}
  };

  const handleTextChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setText(newText);

    // Check verification before allowing compose
    await handleComposeClick();

    if (comboTimeoutRef.current) {
      clearTimeout(comboTimeoutRef.current);
    }

    const newCombo = newText.length > text.length ? combo + 1 : combo > 1 ? combo - 1 : 0;
    setCombo(newCombo);

    if (newCombo > 0 && newCombo % 10 === 0) {
      setComboAnimKey(k => k + 1);
    }

    comboTimeoutRef.current = setTimeout(() => {
      setCombo(0);
    }, 1500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (onCreate) {
      await onCreate(text, file);
      setText('');
      setFile(undefined);
      setCombo(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getComboTier = (count: number) => {
    if (count >= 30) return { text: 'Legendary', className: 'text-purple-400 font-black', pop: true };
    if (count >= 20) return { text: 'Perfect', className: 'text-cyan-400 font-bold', pop: true };
    if (count >= 10) return { text: 'Combo Breaker', className: 'text-orange-400 font-semibold', pop: true };
    if (count > 2) return { text: 'Combo', className: 'text-primary font-medium', pop: false };
    return null;
  };

  useEffect(() => {
    if (textWidthRef.current) {
      setTextWidth(textWidthRef.current.offsetWidth);
    }
  }, [text]);

  // Drag-to-scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!chipsRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - chipsRef.current.offsetLeft);
    setScrollLeft(chipsRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !chipsRef.current) return;
    e.preventDefault();
    const x = e.pageX - chipsRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
    chipsRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Reset hasDragged after a short delay to allow click events to check it
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!chipsRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.touches[0].pageX - chipsRef.current.offsetLeft);
    setScrollLeft(chipsRef.current.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !chipsRef.current) return;
    const x = e.touches[0].pageX - chipsRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
    chipsRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50);
  };

  if (!showComposer) return null;

  return (
    <div 
      className={cn(
        "sticky top-16 w-full z-20 px-2 py-2 sm:px-3 border-b border-border bg-cover bg-center",
      )}
      style={{ backgroundImage: `url(${profile?.banner_image_url || ''})` }}
    >
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-0" />
      <div className="w-full max-w-[1800px] mx-auto px-1 sm:px-2 md:px-4 relative z-10 overflow-hidden">
        <div className="flex items-start gap-3">
          <div className="flex-1 flex items-start gap-3 justify-center min-w-0">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <form onSubmit={handleSubmit} className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <div className="relative flex items-center gap-2 rounded-lg border border-input bg-card/90 backdrop-blur-sm px-3 py-2">
                      {profile?.profile_image_url ? (
                        <Avatar className="w-8 h-8 shrink-0">
                          <AvatarImage src={profile.profile_image_url} alt="Your avatar" />
                          <AvatarFallback className="text-xs">
                            {(profile.display_name || profile.username || '?').charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <Avatar className="w-8 h-8 shrink-0">
                          <AvatarFallback className="text-xs">
                            {(profile?.display_name || profile?.username || '?').charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <input
                        className="flex-1 bg-transparent outline-none text-sm"
                        placeholder="Say something will ya?"
                        value={text}
                        onChange={handleTextChange}
                        onFocus={handleComposeClick}
                        onClick={handleComposeClick}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/mp4"
                        onChange={(e) => setFile(e.target.files?.[0])}
                        className="hidden"
                      />
                      {!hidePostButtons && (
                        <>
                          <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} aria-label="Add content">
                            <ImageIcon className="w-4 h-4" />
                          </Button>
                          <Button type="submit" size="sm">Post</Button>
                        </>
                      )}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {}}
                        aria-label="Contextboard (Coming Soon)"
                      >
                        <Sparkles className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {((categories && mode && onModeChange && onCategoryChange) || viewModeButtons) && (
          <div className="relative">
            {/* Visible row: show ALL/CHRONO + first 4 categories + More */}
            <div
              ref={chipsRef}
              className={cn(
                "mt-2 flex items-center gap-2 overflow-x-auto overflow-y-hidden no-scrollbar px-1 pb-1 relative",
                isDragging ? "cursor-grabbing" : "cursor-grab"
              )}
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                userSelect: 'none'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {categories && mode && onModeChange && onCategoryChange && (
                <>
                  <div className="flex items-center gap-2 mr-1 flex-shrink-0">
                    <Button
                      variant={mode === 'all' ? 'default' : 'outline'}
                      size="sm"
                      className="whitespace-nowrap"
                      onClick={() => onModeChange('all')}
                    >
                      ALL
                      {showRefresh && (
                        <RefreshCw className={`w-3.5 h-3.5 ml-1 animate-spin`} />
                      )}
                    </Button>
                    <Button
                      variant={mode === 'chrono' ? 'default' : 'outline'}
                      size="sm"
                      className="whitespace-nowrap"
                      onClick={() => onModeChange('chrono')}
                    >
                      <motion.span>CHRONO</motion.span>
                    </Button>
                  </div>

                  {/* First 4 categories */}
                  {(categories || []).slice(0, 4).map((c) => (
                    <Button
                      key={c}
                      variant={category === c ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={(e) => {
                        if (hasDragged) {
                          e.preventDefault();
                          return;
                        }
                        onCategoryChange(c);
                      }}
                      className="whitespace-nowrap flex-shrink-0"
                    >
                      {c}
                    </Button>
                  ))}

                  {/* More drawer trigger */}
                  {(categories || []).length > 4 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowMore(true)}
                      className="whitespace-nowrap flex-shrink-0"
                    >
                      More
                    </Button>
                  )}
                </>
              )}
              {viewModeButtons && (
                <div className="flex-shrink-0 ml-auto">
                  {viewModeButtons}
                </div>
              )}
            </div>

            {/* Fade gradient to indicate more */}
            <div 
              className="absolute right-0 top-0 bottom-0 w-20 pointer-events-none"
              style={{
                background: 'linear-gradient(to left, rgba(26, 26, 27, 1) 0%, rgba(26, 26, 27, 0) 100%)'
              }}
            />

            {/* More Drawer: floating/randomized tags */}
            {showMore && (
              <>
                {/* Backdrop covers viewport */}
                <div
                  className="fixed inset-0 z-50 bg-black/50"
                  onClick={() => setShowMore(false)}
                />
                {/* Panel: mobile bottom sheet; desktop centered viewport (ignores sidebar, never clipped) */}
                <div className="fixed inset-0 z-[60] flex items-end md:items-center md:justify-center p-2 md:p-4">
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="tag-drawer-title"
                    className="relative w-full md:w-[720px] max-w-full max-h-[70vh] md:max-h-[80vh] bg-background border border-border rounded-t-xl md:rounded-xl p-3 md:p-4 shadow-2xl"
                  >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 id="tag-drawer-title" className="text-sm font-semibold">Discover Tags</h4>
                      <p className="text-xs text-muted-foreground">Pick a tag to filter the stream</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowMore(false)} aria-label="Close tag drawer">Close</Button>
                  </div>

                  {/* Search */}
                  <div className="mb-3">
                    <input
                      value={tagQuery}
                      onChange={(e) => setTagQuery(e.target.value)}
                      placeholder="Search tags..."
                      className="w-full h-9 px-3 rounded-md bg-background/70 border border-border text-sm outline-none focus:ring-2 focus:ring-primary/40"
                      aria-label="Search tags"
                    />
                  </div>

                  {/* Tag sections */}
                  <div className="relative w-full h-[56vh] md:h-[60vh] overflow-auto no-scrollbar pr-1">
                    {/* Your topics (recent) */}
                    {recentTags.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs text-muted-foreground mb-2">Your topics</div>
                        <div className="flex flex-wrap gap-2">
                          {recentTags.map((tag) => (
                            <Button
                              key={`recent-${tag}`}
                              size="sm"
                              variant={category === tag ? 'secondary' : 'outline'}
                              className="whitespace-nowrap"
                              onClick={() => { onCategoryChange(tag); setShowMore(false); }}
                            >
                              {tag}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quick Picks */}
                    <div className="mb-3">
                      <div className="text-xs text-muted-foreground mb-2">Quick picks</div>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          const pool = [...(categories || [])];
                          for (let i = pool.length - 1; i > 0; i--) {
                            const j = Math.floor(Math.random() * (i + 1));
                            [pool[i], pool[j]] = [pool[j], pool[i]];
                          }
                          return pool.slice(0, 8).map((tag) => (
                            <Button
                              key={`quick-${tag}`}
                              size="sm"
                              variant={category === tag ? 'secondary' : 'outline'}
                              className="whitespace-nowrap"
                              onClick={() => { pushRecentTag(tag); onCategoryChange(tag); setShowMore(false); }}
                            >
                              {tag}
                            </Button>
                          ));
                        })()}
                      </div>
                    </div>

                    {/* All tags grid */}
                    <div className="text-xs text-muted-foreground mb-2">All tags</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(() => {
                        const pool = [...(categories || [])];
                        for (let i = pool.length - 1; i > 0; i--) {
                          const j = Math.floor(Math.random() * (i + 1));
                          [pool[i], pool[j]] = [pool[j], pool[i]];
                        }
                        const filtered = tagQuery
                          ? pool.filter(t => t.toLowerCase().includes(tagQuery.toLowerCase()))
                          : pool;
                        return filtered.slice(0, 60).map((tag) => (
                          <Button
                            key={`all-${tag}`}
                            size="sm"
                            variant={category === tag ? 'secondary' : 'ghost'}
                            className="justify-start w-full"
                            onClick={() => { pushRecentTag(tag); onCategoryChange(tag); setShowMore(false); }}
                          >
                            {tag}
                          </Button>
                        ));
                      })()}
                    </div>
                  </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Verification Modals */}
      <VerificationGateModal
        open={showVerificationGate}
        onClose={() => setShowVerificationGate(false)}
        onGetVerified={handleGetVerified}
        isPending={hasPendingRequest}
        isRejected={isRejected}
      />

      <VerificationForm
        open={showVerificationForm}
        onClose={() => setShowVerificationForm(false)}
        onSuccess={handleVerificationFormSuccess}
      />
    </div>
  );
};

export default Composer;
